/**
 * Round 117 (Phase E3) — Universal Builders index page.
 *
 * ONE page that lists every registered builder and can generate
 * any of them.
 *
 * Round 218A Phase 8D-2 (2026-02-05):
 *   • Adds the ambient Business Credits pill to the page header.
 *   • Sends a stable `attempt_id` on every Generate so the backend
 *     credit gate can dedupe double-clicks / retries at the ledger.
 *   • Opens the Insufficient Credits modal on HTTP 402 and preserves
 *     the customer's current builder + topic so Continue resumes the
 *     exact same generation intent after purchase.
 *   • Refreshes the balance after each successful generation.
 *   • This page uses `mode="draft"` by default so exploration remains
 *     free. A "Generate (uses credits)" toggle lets the customer
 *     promote a draft to a chargeable production run.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, MagicWand } from '@phosphor-icons/react';
import { universalBuilderAPI } from '@/utils/api';
import BuilderProgressOverlay from '@/components/BuilderProgressOverlay';
import UniversalAssetRenderer from '@/components/UniversalAssetRenderer';
import BusinessCreditsPill from '@/components/BusinessCreditsPill';
import InsufficientCreditsModal from '@/components/InsufficientCreditsModal';
import useAttemptId from '@/hooks/useAttemptId';
import { notifyBusinessCreditsChanged } from '@/hooks/useBusinessCredits';

const UniversalBuildersPage = () => {
  const [builders, setBuilders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('draft'); // 'draft' | 'generate'
  const [insufficientOpen, setInsufficientOpen] = useState(false);

  // Attempt-id scope: builder_type + topic. Stays stable across
  // component remounts, network retries, and insufficient-credit
  // interruption. Reset only when the customer switches builders /
  // topics or deliberately retries.
  const scopeKey = selected
    ? `universal:${selected.builder_type}:${(topic || '').trim().toLowerCase()}`
    : '';
  const { attemptId, resetAttemptId, savePendingIntent } = useAttemptId(scopeKey);

  useEffect(() => {
    (async () => {
      try {
        const d = await universalBuilderAPI.list();
        setBuilders(d?.builders || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const runGenerate = async (builderCfg, { forceMode } = {}) => {
    if (!builderCfg) return;
    const activeMode = forceMode || mode;
    setGenerating(true);
    setResult(null);
    try {
      const d = await universalBuilderAPI.generate(builderCfg.builder_type, {
        topic,
        mode: activeMode,
        attemptId,
        assetKind: builderCfg.builder_type,
      });
      setResult(d);
      toast.success(`${d?.label || builderCfg.builder_type} ready.`);
      if (activeMode === 'generate') {
        // Balance changed — refresh the header pill and start a fresh
        // attempt id so the next Generate isn't treated as a duplicate.
        notifyBusinessCreditsChanged();
        resetAttemptId();
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // HTTP 402 → insufficient Business Credits.
      if (status === 402 && detail && detail.error === 'insufficient_business_credits') {
        savePendingIntent({
          route: '/universal-builders',
          builder_type: builderCfg.builder_type,
          topic,
          mode: activeMode,
        });
        setInsufficientOpen(true);
        return;
      }
      console.error(err);
      toast.error(`Failed to generate ${builderCfg.builder_type}. Try again.`);
    } finally {
      setGenerating(false);
    }
  };

  const handleContinueAfterPurchase = async () => {
    setInsufficientOpen(false);
    // Reuse the SAME attempt_id — the backend will short-circuit if
    // the previous attempt somehow charged (idempotency), or run
    // fresh if we never charged (the 402 path never charges).
    await runGenerate(selected, { forceMode: 'generate' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="universal-builders-page">
      <BuilderProgressOverlay visible={generating} title={`Building your ${selected?.label || 'asset'}…`} />

      <InsufficientCreditsModal
        open={insufficientOpen}
        onClose={() => setInsufficientOpen(false)}
        resumeIntent={{
          route: '/universal-builders',
          builder_type: selected?.builder_type,
          topic,
        }}
        onContinueAfterPurchase={handleContinueAfterPurchase}
      />

      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur px-4 sm:px-8 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <MagicWand size={20} className="text-emerald-400" />
            <div>
              <div className="text-sm text-slate-400">Universal Builders</div>
              <h1 className="text-lg font-semibold text-white" data-testid="universal-builders-title">Every builder, one architecture</h1>
            </div>
          </div>
          <BusinessCreditsPill onAddCredits={() => setInsufficientOpen(true)} />
        </div>
      </header>

      <div className="grid lg:grid-cols-[360px_1fr] gap-0">
        {/* Builder list */}
        <aside className="border-r border-slate-800 bg-slate-900/40 p-4 space-y-2 overflow-y-auto">
          <div className="mb-3">
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Topic (optional)</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Pick an angle, e.g. 'holiday launch'"
              data-testid="universal-topic-input"
              className="w-full rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-400"
            />
          </div>
          <div className="mb-3 flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/60 p-2 text-xs">
            <span className="text-slate-400">Mode</span>
            <div className="inline-flex overflow-hidden rounded-md border border-slate-700">
              <button
                type="button"
                onClick={() => setMode('draft')}
                className={`px-2 py-1 ${mode === 'draft' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid="universal-mode-draft"
              >
                Draft (free)
              </button>
              <button
                type="button"
                onClick={() => setMode('generate')}
                className={`px-2 py-1 ${mode === 'generate' ? 'bg-emerald-500 text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid="universal-mode-generate"
              >
                Generate
              </button>
            </div>
          </div>
          {builders.map((b) => (
            <button
              key={b.builder_type}
              onClick={() => { setSelected(b); runGenerate(b); }}
              disabled={generating}
              data-testid={`builder-tile-${b.builder_type}`}
              className={`w-full text-left rounded-lg border p-3 transition ${
                selected?.builder_type === b.builder_type
                  ? 'border-emerald-400/70 bg-emerald-500/10'
                  : 'border-slate-800 bg-slate-900/60 hover:bg-slate-800'
              } disabled:opacity-40`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-100">{b.label}</div>
                <ArrowRight size={14} className="text-slate-500" />
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">
                sections: {b.section_kinds.join(', ')}
              </div>
            </button>
          ))}
        </aside>

        {/* Preview */}
        <main className="overflow-y-auto min-h-[calc(100vh-4rem)]" data-testid="universal-preview-pane">
          {!result && !generating && (
            <div className="p-10 text-slate-500 text-sm">
              Pick a builder on the left. Every result inherits your Business Brain, Brand Brain, Market Intelligence and approved assets automatically.
            </div>
          )}
          {result && (
            <UniversalAssetRenderer
              payload={result.payload}
              label={result.label}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default UniversalBuildersPage;
